import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { format, parse } from 'date-fns';
import { DatepickerProvider } from '../../Datepicker/context/context';
import { Datepicker } from '../../Datepicker/Datepicker';
import { useDashboard } from '../contexts/DashboardContext';
import { WidgetRenderer } from '../WidgetRenderer/WidgetRenderer';
import { DebouncedTextInput } from '../DebouncedTextInput';
import { Widget, DashboardFilter } from '../types';
import { ReactComponent as Settingsicon } from '../assets/settings.svg';
import { ReactComponent as CopyIcon } from '../assets/copy.svg';
import { ReactComponent as DeleteIcon } from '../assets/delete.svg';
import { ReactComponent as RefreshIcon } from '../assets/refresh.svg';
import { Tooltip } from 'react-tooltip';
import * as Sentry from '@sentry/react';
import { useMultiSelectComponents, singleSelectComponents } from '../multiSelectComponents';
import { useCurrentUser } from '../../../contexts/CurrentUser';
import { downloadWidgetAsSVG, downloadWidgetAsJPEG } from '../../../utils/downloadWidget';
import { WidgetDownloadMenu, WidgetDownloadFormat } from './WidgetDownloadMenu';
import { ampli } from '../../../ampli';
import styles from './WidgetCard.module.scss';
import { observeResize } from '../observeResize';
import { is } from 'date-fns/locale';

// Chart widgets that render as a single ECharts SVG can be exported as vector SVG.
// Everything else (table, metric, card_grid, text, opinions) is JPEG-only.
const SVG_EXPORTABLE_TYPES = new Set(['bar', 'line', 'pie', 'map', 'word_cloud']);

interface WidgetCardProps {
  widget: Widget;
  dragHandleClass: string;
}

export const WidgetCard: React.FC<WidgetCardProps> = ({
  widget,
  dragHandleClass,
}) => {
  const { t } = useTranslation();

  const multiSelectComponents = useMultiSelectComponents();
  const {
    isEditing, isAdmin, selectWidget, removeWidget, copyWidget, executeWidgetQuery,
    invalidateWidgetCache, displayedDashboard, filterValues, filterOptions, setFilterValue,
  } = useDashboard();
  const [currentUser] = useCurrentUser();

  // Widget-level filters: filters scoped to this specific widget
  const widgetFilters = useMemo(
    () => (displayedDashboard?.filters ?? [])
      .filter((f) => f.widgetId === widget.id && !f.hidden)
      .sort((a, b) => a.order - b.order),
    [displayedDashboard?.filters, widget.id]
  );

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    selectWidget(widget.id);
  };

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await copyWidget(widget.id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    removeWidget(widget.id);
  };

  // Auto-populate widget-level filters once options load (mirrors FilterBar logic).
  // Use a ref to track which filters have been auto-populated to avoid re-triggering.
  const populatedFiltersRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    widgetFilters.forEach((filter) => {
      if (populatedFiltersRef.current.has(filter.parameterName)) return;
      const currentValue = filterValues[filter.parameterName];
      if (currentValue != null) {
        populatedFiltersRef.current.add(filter.parameterName);
        return;
      }

      if (filter.filterType === 'date_range') {
        populatedFiltersRef.current.add(filter.parameterName);
        if (filter.defaultValue?.from && filter.defaultValue?.to) {
          setFilterValue(filter.parameterName, filter.defaultValue);
        } else {
          const year = new Date().getFullYear();
          setFilterValue(filter.parameterName, { from: `${year}-01-01`, to: `${year}-12-31` });
        }
        return;
      }

      if (filter.filterType !== 'select' && filter.filterType !== 'multi_select') return;

      const optionsResult = filterOptions[filter.id];
      if (!optionsResult?.options?.length) return;

      populatedFiltersRef.current.add(filter.parameterName);
      const opts = optionsResult.options;
      const firstKey = Object.keys(opts[0])[0];
      if (!firstKey) return;

      if (filter.config?.allow_all) {
        setFilterValue(filter.parameterName, '__all__');
      } else {
        setFilterValue(filter.parameterName, [opts[0][firstKey]]);
      }
    });
  }, [widgetFilters, filterOptions, filterValues, setFilterValue]);

  // Pagination state for table widgets
  const configPageSize = widget.config.pageSize;
  const [currentPage, setCurrentPage] = useState(1);
  const [isInvalidating, setIsInvalidating] = useState(false);

  // Reset to page 1 when filter values change
  const filterValuesKey = JSON.stringify(filterValues);
  useEffect(() => {
    setCurrentPage(1);
  }, [filterValuesKey]);

  const handlePageChange = useCallback(async (newPage: number) => {
    setCurrentPage(newPage);
    await executeWidgetQuery(widget.id, newPage, configPageSize);
  }, [widget.id, configPageSize, executeWidgetQuery]);

  // Widget-level filter change: set value (re-execution handled by context)
  const handleWidgetFilterChange = useCallback((parameterName: string, value: any) => {
    if (!isEditing) {
      const filter = widgetFilters.find((f) => f.parameterName === parameterName);
      ampli.track({
        event_type: 'Use dashboard filter',
        event_properties: {
          user_id: currentUser?.id,
          workspace_id: currentUser?.workspace_id,
          dashboard_id: displayedDashboard?.id,
          filter_type: filter?.filterType,
          filter_scope: 'widget',
          is_super_admin: currentUser?.is_super_admin ?? false,
        },
      });
    }
    setFilterValue(parameterName, value);
    setCurrentPage(1);
  }, [isEditing, widgetFilters, currentUser, displayedDashboard?.id, setFilterValue]);

  // Scroll carousel for widget filters
  const filtersScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = filtersScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = filtersScrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    const stopObserving = observeResize(el, checkScroll);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      stopObserving();
    };
  }, [checkScroll, widgetFilters.length]);

  const scrollFilters = useCallback((dir: 'left' | 'right') => {
    filtersScrollRef.current?.scrollBy({ left: dir === 'right' ? 220 : -220, behavior: 'smooth' });
  }, []);

  const handleRefresh = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentPage(1);
    await executeWidgetQuery(widget.id, configPageSize ? 1 : undefined, configPageSize);
  };

  const handleInvalidateCache = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isInvalidating) return;
    setIsInvalidating(true);
    try {
      await invalidateWidgetCache(widget.id);
      toast.success(t('Cache invalidated') as string);
      setCurrentPage(1);
      await executeWidgetQuery(widget.id, configPageSize ? 1 : undefined, configPageSize);
    } catch {
      toast.error(t('Failed to invalidate cache') as string);
    } finally {
      setIsInvalidating(false);
    }
  };

  const isCached = widget.data?.cached === true;

  // Image download (SVG for chart widgets, JPEG for all widget types)
  const bodyRef = useRef<HTMLDivElement>(null);

  const canExportSvg = SVG_EXPORTABLE_TYPES.has(widget.chartType);
  const hasRenderableContent =
    widget.chartType === 'text'
      ? true
      : Boolean(widget.data && !widget.data.error && (widget.data.rows?.length ?? 0) > 0);

  const handleDownload = async (fmt: WidgetDownloadFormat) => {
    const node = bodyRef.current;
    if (!node) return;
    const name = widget.title?.trim() || `widget-${widget.id}`;

    // SVG export and direct chart-SVG rasterization are near-instant. HTML widgets go
    // through html2canvas, which can take a few seconds, so show a progress toast there.
    const showProgress = fmt === 'jpeg' && !canExportSvg;
    const toastId = showProgress ? toast.loading(t('Preparing image…') as string) : null;

    try {
      if (fmt === 'svg' && node.querySelector('svg')) {
        downloadWidgetAsSVG(node, name);
      } else if (fmt === 'svg') {
        // A chart widget showing an HTML view (the word cloud's list) has no SVG to export.
        await downloadWidgetAsJPEG(node, name);
        toast.info(t('This view has no vector version. Saved as JPEG.') as string);
      } else {
        await downloadWidgetAsJPEG(node, name, { vector: canExportSvg });
      }
      if (toastId !== null) {
        toast.update(toastId, {
          render: t('Image ready') as string,
          type: 'success',
          isLoading: false,
          autoClose: 2000,
        });
      }
      ampli.track({
        event_type: 'Download dashboard widget',
        event_properties: {
          user_id: currentUser?.id,
          workspace_id: currentUser?.workspace_id,
          dashboard_id: displayedDashboard?.id,
          widget_id: widget.id,
          widget_type: widget.chartType,
          format: fmt,
        },
      });
    } catch (error) {
      Sentry.captureException(error);
      const message = t('Failed to download widget') as string;
      if (toastId !== null) {
        toast.update(toastId, { render: message, type: 'error', isLoading: false, autoClose: 4000 });
      } else {
        toast.error(message);
      }
    }
  };

  return (
    <div className={styles.card}>
      <div className={`${styles.header} ${isEditing ? dragHandleClass : ''}`}>
        <span className={styles.title}>
          {widget.title}
          {isAdmin && isCached && (
            <span className={styles.cachedBadge} title={t('Served from cache')}>
              {t('cached')}
            </span>
          )}
        </span>
        <div className={styles.actions}>
          {isAdmin && !isEditing && isCached && (
            <button
              className={styles.actionBtn}
              onClick={handleInvalidateCache}
              disabled={isInvalidating}
              data-tooltip-id="widget-card-tooltip"
              data-tooltip-content={t('Invalidate cache')}
            >
              &#128465;
            </button>
          )}
          {widget.chartType !== 'text' && (
            <button
              className={styles.actionBtn}
              onClick={handleRefresh}
              data-tooltip-id="widget-card-tooltip"
              data-tooltip-content={t('Refresh')}
            >
              <RefreshIcon />
            </button>
          )}
          {hasRenderableContent && (
            <WidgetDownloadMenu
              canExportSvg={canExportSvg}
              onDownload={handleDownload}
              triggerClassName={styles.actionBtn}
            />
          )}
          {isEditing && (
            <>
              <button
                className={styles.actionBtn}
                onClick={handleEdit}
                data-tooltip-id="widget-card-tooltip"
                data-tooltip-content={t('Edit')}
              >
                <Settingsicon />
              </button>
              <button
                className={styles.actionBtn}
                onClick={handleCopy}
                data-tooltip-id="widget-card-tooltip"
                data-tooltip-content={t('Copy')}
              >
                <CopyIcon />
              </button>
              <button
                className={`${styles.actionBtn} ${styles.deleteBtn}`}
                onClick={handleDelete}
                data-tooltip-id="widget-card-tooltip"
                data-tooltip-content={t('Delete')}
              >
                <DeleteIcon />
              </button>
            </>
          )}
          <Tooltip id="widget-card-tooltip" place="bottom" style={{'zIndex': 2000}}/>
        </div>
      </div>
      {widgetFilters.length > 0 && (
        <div className={styles.widgetFiltersWrapper}>
          {canScrollLeft && (
            <>
              <div className={styles.fadeLeft} />
              <button
                className={`${styles.scrollBtn} ${styles.scrollBtnLeft}`}
                onClick={() => scrollFilters('left')}
                aria-label={t('Scroll filters left')}
                type="button"
              />
            </>
          )}
          <div className={styles.widgetFilters} ref={filtersScrollRef}>
          {widgetFilters.map((filter) => {
            const optResult = filterOptions[filter.id];
            const options = optResult?.options ?? [];
            const selectOpts = options.map((opt) => {
              const keys = Object.keys(opt);
              const rawValue = keys.length > 0 ? opt[keys[0]] : '';
              const rawLabel = keys.length > 1 ? opt[keys[1]] : rawValue;
              return { value: rawValue as any, label: String(rawLabel ?? ''), stringValue: String(rawValue ?? '') };
            });
            const currentValue = filterValues[filter.parameterName];

            if (filter.filterType === 'date_range') {
              const dateValue = currentValue ?? {};
              const startDate = dateValue.from
                ? parse(dateValue.from, 'yyyy-MM-dd', new Date())
                : null;
              const endDate = dateValue.to
                ? parse(dateValue.to, 'yyyy-MM-dd', new Date())
                : null;

              return (
                <div key={filter.id} className={styles.widgetFilterItem}>
                  <label className={styles.widgetFilterLabel}>{filter.title}</label>
                  <DatepickerProvider>
                    <Datepicker
                      onChange={(start: Date | null, end: Date | null) => {
                        if (!start && !end) {
                          handleWidgetFilterChange(filter.parameterName, undefined);
                          return;
                        }
                        const newValue: Record<string, string> = {};
                        if (start) newValue.from = format(start, 'yyyy-MM-dd');
                        if (end) newValue.to = format(end, 'yyyy-MM-dd');
                        handleWidgetFilterChange(filter.parameterName, Object.keys(newValue).length > 0 ? newValue : undefined);
                      }}
                      initStartDate={startDate}
                      initEndDate={endDate}
                      isRange
                      className={styles.widgetFilterDatepicker}
                    />
                  </DatepickerProvider>
                </div>
              );
            }

            if (filter.filterType === 'multi_select') {
              const allOption = filter.config?.allow_all
                ? { value: '__all__' as any, label: t('All'), stringValue: '__all__' }
                : null;
              const fullOpts = allOption ? [allOption, ...selectOpts] : selectOpts;

              const isAll = currentValue === '__all__';
              const selected = isAll
                ? (allOption ? [allOption] : [])
                : Array.isArray(currentValue)
                  ? selectOpts.filter((o) => currentValue.some((v: any) => String(v) === o.stringValue))
                  : [];

              const defaultOptionValues = allOption
                ? ['__all__']
                : selectOpts.length > 0 ? [String(selectOpts[0].value)] : [];

              return (
                <div key={filter.id} className={styles.widgetFilterItem}>
                  <label className={styles.widgetFilterLabel}>{filter.title}</label>
                  <Select
                    className="wf-select-container"
                    classNamePrefix="wf-select"
                    options={fullOpts}
                    value={selected}
                    getOptionValue={(o: any) => o.stringValue}
                    getOptionLabel={(o: any) => o.label}
                    onChange={(opts, actionMeta) => {
                      if ((actionMeta as any)?.action === 'clear') {
                        handleWidgetFilterChange(filter.parameterName, allOption ? '__all__' : (selectOpts.length > 0 ? [selectOpts[0].value] : undefined));
                        return;
                      }
                      const selectedOpts = opts as any[];
                      if (selectedOpts?.some((o: any) => o.value === '__all__')) {
                        handleWidgetFilterChange(filter.parameterName, '__all__');
                        return;
                      }
                      const values = selectedOpts?.map((o: any) => o.value) ?? [];
                      handleWidgetFilterChange(filter.parameterName, values.length > 0 ? values : (allOption ? '__all__' : undefined));
                    }}
                    isMulti
                    hideSelectedOptions={false}
                    closeMenuOnSelect={false}
                    isSearchable
                    placeholder={filter.title}
                    isLoading={!optResult}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                    components={multiSelectComponents}
                    {...({ defaultOptionValues } as any)}
                  />
                </div>
              );
            }

            if (filter.filterType === 'text') {
              return (
                <div key={filter.id} className={styles.widgetFilterItem}>
                  <label className={styles.widgetFilterLabel}>{filter.title}</label>
                  <DebouncedTextInput
                    className={styles.widgetFilterInput}
                    value={currentValue ?? ''}
                    onChange={(val) => handleWidgetFilterChange(filter.parameterName, val && val.trim() ? val : undefined)}
                    placeholder={filter.title}
                  />
                </div>
              );
            }

            // Default: 'select' (single select)
            const allOption = filter.config?.allow_all
              ? { value: '__all__' as any, label: t('All'), stringValue: '__all__' }
              : null;
            const fullOpts = allOption ? [allOption, ...selectOpts] : selectOpts;

            const scalarValue = Array.isArray(currentValue) ? currentValue[0] : currentValue;
            const selected = currentValue === '__all__'
              ? allOption
              : scalarValue != null
                ? selectOpts.find((o) => o.stringValue === String(scalarValue)) ?? null
                : null;

            return (
              <div key={filter.id} className={styles.widgetFilterItem}>
                <label className={styles.widgetFilterLabel}>{filter.title}</label>
                <Select
                  className="wf-select-container"
                  classNamePrefix="wf-select"
                  options={fullOpts}
                  value={selected}
                  getOptionValue={(o) => o.stringValue}
                  getOptionLabel={(o) => o.label}
                  onChange={(opt) => handleWidgetFilterChange(filter.parameterName, opt ? (opt.value === '__all__' ? '__all__' : [opt.value]) : (allOption ? '__all__' : undefined))}

                  isSearchable
                  placeholder={filter.title}
                  isLoading={!optResult}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                  components={singleSelectComponents}
                />
              </div>
            );
          })}
          </div>
          {canScrollRight && (
            <>
              <div className={styles.fadeRight} />
              <button
                className={`${styles.scrollBtn} ${styles.scrollBtnRight}`}
                onClick={() => scrollFilters('right')}
                aria-label={t('Scroll filters right')}
                type="button"
              />
            </>
          )}
        </div>
      )}
      <div className={`${styles.body} ${widget.chartType === 'table' ? styles.bodyTable : ''}`} ref={bodyRef}>
        <WidgetRenderer
          widget={widget}
          onSetFilter={setFilterValue}
          page={configPageSize ? currentPage : undefined}
          pageSize={configPageSize}
          onPageChange={configPageSize ? handlePageChange : undefined}
        />
      </div>
    </div>
  );
};
